/**
 * Hook das despesas detalhadas por linha DRE (Task #124 — /api/v2/despesas).
 *
 * Mesmo padrão keyed de useDreV2: a lista depende de (linha_dre, regime,
 * período, mês). Trocar a linha selecionada refaz o fetch. TTL 1 min.
 */
import { useEffect, useState } from "react";
import { getDespesasV2 } from "../api/client";
import type { DespesasV2Response } from "../api/types";
import { startVisiblePolling } from "./visiblePolling";

const REFETCH_MS = 180 * 1000; // #75: 60s->180s (fonte muda via cron diario + sync manual)

interface CacheEntry {
  expiresAt: number;
  promise: Promise<DespesasV2Response>;
}

const cache = new Map<string, CacheEntry>();

export function invalidateDespesasV2Cache(): void {
  cache.clear();
}

export interface UseDespesasV2Params {
  linhaDre: string;
  regime: string;
  periodo: string;
  mesRef?: string;
}

export interface UseDespesasV2State {
  data: DespesasV2Response | null;
  isLoading: boolean;
  error: Error | null;
}

export function useDespesasV2({
  linhaDre,
  regime,
  periodo,
  mesRef,
}: UseDespesasV2Params): UseDespesasV2State {
  const [state, setState] = useState<UseDespesasV2State>({
    data: null,
    isLoading: true,
    error: null,
  });

  useEffect(() => {
    let cancel = false;
    const key = `${linhaDre}|${regime}|${periodo}|${mesRef ?? ""}`;

    function load() {
      const now = Date.now();
      let entry = cache.get(key);
      if (!entry || entry.expiresAt < now) {
        entry = {
          expiresAt: now + REFETCH_MS,
          promise: getDespesasV2({
            linha_dre_codigo: linhaDre,
            regime,
            periodo,
            mes_ref: mesRef,
          }),
        };
        cache.set(key, entry);
      }
      entry.promise
        .then((data) => {
          if (cancel) return;
          setState({ data, isLoading: false, error: null });
        })
        .catch((err: Error) => {
          if (cancel) return;
          cache.delete(key);
          setState({ data: null, isLoading: false, error: err });
        });
    }

    setState((s) => ({ ...s, isLoading: true }));
    load();
    // #75: tick skipa com a aba em background; volta visivel/focus -> 1 load.
    const stop = startVisiblePolling(load, REFETCH_MS);
    return () => {
      cancel = true;
      stop();
    };
  }, [linhaDre, regime, periodo, mesRef]);

  return state;
}
